import {
  angleMarkRadius,
  angleMarkValuePosition,
  distanceTo,
  type Evaluated,
} from '@/domain/construction';
import { conics, lines, polygon as polygonOps } from '@/domain/geometry';
import { type Vec2, vec } from '@/domain/math';
import { drawOrderOf, type EntityId, type EntityKind, inDrawOrder, kindOf } from '@/domain/model';

import { type Scene, valueOf } from './Scene';
import { queryIndex, queryIndexBounds } from './spatialIndex';

/**
 * Picking.
 *
 * Runs entirely in **world space** with a tolerance the caller has already converted from
 * screen pixels (`camera.screenToWorldDistance`). That is what makes clicking feel
 * identical at every zoom level: the tolerance shrinks in world units exactly as fast as
 * the drawing grows on screen.
 *
 * ## Priority, not just distance
 *
 * Nearest-wins is wrong for a geometry editor. A vertex sits *exactly on* the two segments
 * that meet there and on any circle through it, so distance alone is a tie at zero and the
 * winner would be whichever happened to be drawn last. Since the vertex is almost always
 * what the user meant — it is the thing you drag, the thing constructions attach to — kinds
 * are ranked, and only ties within a rank fall through to distance.
 */

/** Lower sorts first. */
const KIND_PRIORITY: Readonly<Record<EntityKind, number>> = {
  point: 0,
  text: 1,
  measurement: 1,
  segment: 2,
  ray: 2,
  line: 2,
  vector: 2,
  arc: 2,
  circle: 2,
  ellipse: 2,
  polyline: 2,
  // Regions come last: clicking inside a filled polygon must not beat its own edge.
  polygon: 3,
  /*
   * With the labels, not above them.
   *
   * It was first given the top priority, reasoning that a mark is small and sits where
   * several objects already meet. That reasoning had it backwards: the mark's pick target is
   * *on its own arc*, not at the vertex, so it does not compete with the point it annotates —
   * and giving it the top priority made it steal every click at the vertex, so a marked
   * right angle could no longer be dragged. Which is the one thing the mark exists to show.
   */
  'angle-mark': 1,
  /*
   * A plane sits below the solid it cuts.
   *
   * It is a wash the size of a face, and everything a figure draws on it — the corners of the
   * section, the giao tuyến, the height — is inside it. On the ordinary distance rule the wash
   * counts as a hit at zero distance everywhere inside itself, so any higher priority would let
   * it swallow every click on the construction it exists to explain.
   */
  plane: 4.5,
  // A solid sits below the flat shapes: it is usually the largest thing on the canvas, and
  // anything drawn on top of it should win the click.
  solid: 3.5,
  image: 4,
  // With the other curves: a graph is a line you click on, and it competes with them on
  // distance rather than outranking them.
  'function-graph': 2,
  /*
   * With the points, above the curve they sit on.
   *
   * A marked point is *on* the graph, so on distance alone the two tie at every dot and the
   * curve — written first — would take the click. The dots are the smaller, more deliberate
   * target and the one a user is reaching for when they click exactly there.
   */
  'graph-marks': 0,
  /*
   * Below everything, and it matters.
   *
   * Axes cross the whole figure, so on the ordinary distance rule they would win the click at
   * every point that happens to sit on one — which for a graph is the origin, every root, and
   * every intercept. Those are precisely the points a lesson is about.
   */
  axes: 5,
  /*
   * A table is a block, and it is picked anywhere inside it. Last, so anything drawn on top
   * of it still wins: it is a caption placed on the page, and the drawing comes first.
   */
  'variation-table': 6,
};

export interface Hit {
  readonly id: EntityId;
  readonly kind: EntityKind;
  /** World-space distance from the query point to the entity. */
  readonly distance: number;
  /** The point on the entity nearest the query — where a snap indicator belongs. */
  readonly closest: Vec2;
}

/**
 * Distance from `world` to an evaluated value, and the nearest point on it.
 *
 * Returns `null` for values with no pickable geometry.
 */
export function distanceToValue(
  value: Evaluated,
  world: Vec2
): { readonly distance: number; readonly closest: Vec2 } | null {
  switch (value.kind) {
    // A viewpoint: no geometry on the page at all.
    case 'space':
      return null;

    case 'point':
      return { distance: vec.distance(world, value.point), closest: value.point };

    case 'text':
    case 'measurement':
      // Labels are picked by their anchor; a proper text bounding box needs measured
      // metrics, which belong with the text tooling in M13.
      return { distance: vec.distance(world, value.position), closest: value.position };

    /*
     * A picture is picked anywhere on it, like the filled shapes and the variation table.
     *
     * It used to share the anchor rule above, which for a label is a reasonable stand-in and for
     * a picture is simply wrong: the anchor is one corner of something the size of a hand, so
     * the only way to select it was to click within a few pixels of its bottom-left corner.
     * Every other part of the editor already treated it as the rectangle it is — the painter
     * draws that rectangle and `boundsOfValue` measures it — so this was the one place that
     * disagreed, and the drawing gave the user no way to guess it.
     */
    case 'image': {
      const left = value.position.x;
      const right = left + value.width;
      const bottom = value.position.y;
      const top = bottom + value.height;

      const closest = vec.vec2(
        Math.min(Math.max(world.x, left), right),
        Math.min(Math.max(world.y, bottom), top)
      );
      const inside = world.x >= left && world.x <= right && world.y >= bottom && world.y <= top;
      return { distance: inside ? 0 : vec.distance(world, closest), closest };
    }

    /*
     * A solid is picked by its nearest drawn edge, hidden ones included. Excluding hidden
     * edges would make the far corner of a cube unclickable, and the user has no way to
     * know that the line they can see is not the line the hit test considers.
     */
    /*
     * Picked **on its arc**, not at its vertex.
     *
     * Picking at the vertex put the mark exactly on top of the point it annotates, and the
     * point always lost. A marked right angle then could not be dragged at all — which is
     * precisely the demonstration the mark exists for.
     *
     * Where it is drawn comes from `angleMarkRadius`, shared with the painter and the caption
     * anchor. It used to be worked out here as a third of the shorter arm, which is only one of
     * the two limits the arc obeys — so on any figure with long arms the target sat well outside
     * the mark and clicking it selected nothing.
     *
     * ## And on its number, when it has one
     *
     * Two targets, whichever is nearer. The measure can be dragged well away from its own arc —
     * that is what dragging it is *for* — and a mark whose number is over there while its only
     * pick point is back on the arc hands the user a piece of text they cannot get hold of a
     * second time. It also means clicking `56.3°` selects the mark, which is what anyone
     * reaching for the number expects.
     */
    case 'angle-mark': {
      const at = angleMarkPickPoint(value);
      let best = { distance: vec.distance(world, at), closest: at };

      if (value.showValue) {
        const number = angleMarkValuePosition(value);
        const distance = number === null ? Infinity : vec.distance(world, number);
        if (number !== null && distance < best.distance) best = { distance, closest: number };
      }
      return best;
    }

    case 'solid': {
      let best: { distance: number; closest: Vec2 } | null = null;
      for (const edge of [...value.visible, ...value.hidden]) {
        const closest = lines.closestPointOnSegment(edge, world);
        const distance = vec.distance(world, closest);
        if (best === null || distance < best.distance) best = { distance, closest };
      }
      return best;
    }

    case 'polygon': {
      const closest = closestOnRing(value.vertices, world);
      if (closest === null) return null;
      // Inside the ring counts as a hit at zero distance, so a filled shape can be
      // grabbed anywhere — but its edge still wins on priority.
      const inside = polygonOps.containsPoint(value.vertices, world);
      return { distance: inside ? 0 : vec.distance(world, closest), closest };
    }

    case 'polyline': {
      const closest = value.closed
        ? closestOnRing(value.points, world)
        : closestOnPath(value.points, world);
      if (closest === null) return null;
      return { distance: vec.distance(world, closest), closest };
    }

    /*
     * Nearest point on any of the drawn pieces.
     *
     * Measured against the *sampled* path rather than solved analytically, which is the honest
     * thing: the curve on screen is the sampled path, and a hit test that used the true
     * function would disagree with what the user can see near a sharp turn.
     */
    /*
     * Picked on a dot, and only on a dot.
     *
     * Not on the guides: they run from a point to the axes and cross the whole quadrant between,
     * so picking them would make a click anywhere along two long lines select the marks. The dots
     * are what the object *is*; the guides are how it is drawn. The numbers are not pickable for
     * the same reason the axes' own are not.
     */
    case 'graph-marks': {
      let best: { distance: number; closest: Vec2 } | null = null;
      for (const point of value.points) {
        const distance = vec.distance(world, point);
        if (best === null || distance < best.distance) best = { distance, closest: point };
      }
      return best;
    }

    // Picked on a line or a tick, whichever is nearer. The numbers are not pickable: they are
    // annotations on the axes rather than parts of them.
    case 'axes': {
      let best: { distance: number; closest: Vec2 } | null = null;
      for (const segment of [value.xAxis, value.yAxis, ...value.ticks]) {
        const closest = lines.closestPointOnSegment(segment, world);
        const distance = vec.distance(world, closest);
        if (best === null || distance < best.distance) best = { distance, closest };
      }
      return best;
    }

    /*
     * The curve, and the formula written beside it.
     *
     * The label is offered as a target of its own, exactly as an angle mark's number is, and for
     * the same reason: a user reaching for the formula means the formula. `closest` is what says
     * which of the two was hit, and the drag gesture reads it back rather than measuring again —
     * so picking and dragging can never disagree about what was grabbed.
     */
    case 'function-graph': {
      let best: { distance: number; closest: Vec2 } | null = null;
      for (const segment of value.segments) {
        const closest = closestOnPath(segment, world);
        if (closest === null) continue;
        const distance = vec.distance(world, closest);
        if (best === null || distance < best.distance) best = { distance, closest };
      }

      if (value.labelAt !== null) {
        const distance = vec.distance(world, value.labelAt);
        if (best === null || distance < best.distance) best = { distance, closest: value.labelAt };
      }
      return best;
    }

    /*
     * Picked anywhere inside the frame, like a filled shape rather than like a set of lines.
     *
     * Picking it on its rules would mean a user has to hit a hairline to move their table,
     * and the cells they would naturally grab — the empty middle of the `y` row — would do
     * nothing. A block is grabbed by its face.
     */
    case 'variation-table': {
      const left = value.corner.x;
      const right = left + value.width;
      const bottom = value.corner.y - value.height;
      const top = value.corner.y;

      const closest = vec.vec2(
        Math.min(Math.max(world.x, left), right),
        Math.min(Math.max(world.y, bottom), top)
      );
      const inside = world.x >= left && world.x <= right && world.y >= bottom && world.y <= top;
      return { distance: inside ? 0 : vec.distance(world, closest), closest };
    }

    case 'segment':
    case 'ray':
    case 'line':
    case 'vector':
    case 'circle':
    case 'arc':
    case 'ellipse': {
      const curve = curveOf(value);
      if (curve === null) return null;
      const closest = closestOnCurve(value, world);
      return { distance: distanceTo(curve, world), closest };
    }
  }
}

function curveOf(value: Evaluated) {
  switch (value.kind) {
    case 'angle-mark':
    case 'function-graph':
    case 'axes':
    case 'variation-table':
    case 'graph-marks':
      return null;

    // Unreachable — only the curve branch calls this — but spelled out so that adding a
    // value kind keeps failing the compile until it has been thought about.
    case 'solid':
    case 'space':
      return null;

    case 'segment':
      return { kind: 'segment', segment: value.segment } as const;
    case 'vector':
      return { kind: 'segment', segment: { a: value.from, b: value.to } } as const;
    case 'ray':
      return { kind: 'ray', ray: value.ray } as const;
    case 'line':
      return { kind: 'line', line: value.line } as const;
    case 'circle':
      return { kind: 'circle', circle: value.circle } as const;
    case 'arc':
      return { kind: 'arc', arc: value.arc } as const;
    case 'ellipse':
      return { kind: 'ellipse', ellipse: value.ellipse } as const;
    case 'point':
    case 'polygon':
    case 'polyline':
    case 'text':
    case 'measurement':
    case 'image':
      return null;
  }
}

function closestOnCurve(value: Evaluated, world: Vec2): Vec2 {
  switch (value.kind) {
    // Nothing to be close to; the caller only reaches here for values `distanceToValue` accepted.
    case 'space':
      return world;

    case 'axes':
      return value.origin;

    case 'function-graph':
      return value.segments[0]?.[0] ?? world;

    case 'graph-marks': {
      let best: Vec2 | null = null;
      for (const point of value.points) {
        if (best === null || vec.distance(world, point) < vec.distance(world, best)) best = point;
      }
      return best ?? world;
    }

    case 'solid':
      return value.origin;

    case 'angle-mark':
      return value.vertex;

    case 'segment':
      return lines.closestPointOnSegment(value.segment, world);
    case 'vector':
      return lines.closestPointOnSegment({ a: value.from, b: value.to }, world);
    case 'ray':
      return lines.closestPointOnRay(value.ray, world);
    case 'line':
      return lines.closestPointOnLine(value.line, world);
    case 'circle':
      return conics.closestPointOnCircle(value.circle, world);
    case 'arc':
      return conics.closestPointOnArc(value.arc, world);
    case 'ellipse':
      return conics.closestPointOnEllipse(value.ellipse, world);
    case 'point':
    case 'polygon':
    case 'polyline':
    case 'text':
    case 'measurement':
    case 'image':
    case 'variation-table':
      // Not a curve; callers only reach this through `curveOf` returning non-null.
      return world;
  }
}

function closestOnPath(points: readonly Vec2[], world: Vec2): Vec2 | null {
  if (points.length === 0) return null;
  if (points.length === 1) return points[0] ?? null;

  let best: Vec2 | null = null;
  let bestDistance = Infinity;
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1];
    const b = points[i];
    if (a === undefined || b === undefined) continue;
    const candidate = lines.closestPointOnSegment({ a, b }, world);
    const distance = vec.distance(world, candidate);
    if (distance < bestDistance) {
      bestDistance = distance;
      best = candidate;
    }
  }
  return best;
}

function closestOnRing(points: readonly Vec2[], world: Vec2): Vec2 | null {
  if (points.length < 2) return points[0] ?? null;
  let best: Vec2 | null = null;
  let bestDistance = Infinity;
  for (const edge of polygonOps.edges(points)) {
    const candidate = lines.closestPointOnSegment(edge, world);
    const distance = vec.distance(world, candidate);
    if (distance < bestDistance) {
      bestDistance = distance;
      best = candidate;
    }
  }
  return best;
}

export interface HitTestOptions {
  /** Skip these entities. Used while dragging so a point does not snap to itself. */
  readonly exclude?: ReadonlySet<EntityId>;
  /** Consider only entities passing this test. */
  readonly filter?: (id: EntityId, kind: EntityKind) => boolean;
  /** Include entities marked `locked`. Off by default: locked means unpickable. */
  readonly includeLocked?: boolean;
}

/**
 * Everything within `tolerance`, best first.
 *
 * Ordered by kind priority, then distance, then reverse draw order so the topmost of two
 * coincident entities wins.
 *
 * ## Which entities are even considered
 *
 * The scene's spatial index narrows the field to entities whose bounds are near the cursor,
 * so the cost is proportional to what is *under* the pointer rather than to the size of the
 * drawing. It was a linear sweep until M14, which measured 3 ms per query at 8 000 entities —
 * on a path that runs on every pointer move.
 *
 * The index answers with a **superset**, never a decision: a bounding box overlapping the
 * tolerance circle does not mean the geometry does. Every candidate below is still measured
 * exactly, so the index can only ever cost time, never change an answer.
 */
export function hitTestAll(
  scene: Scene,
  world: Vec2,
  tolerance: number,
  options: HitTestOptions = {}
): readonly Hit[] {
  const hits: (Hit & { order: number })[] = [];
  const ranks = drawOrderOf(scene.document);

  for (const id of queryIndex(scene.index, world, tolerance)) {
    const entity = scene.document.entities.get(id);
    if (entity === undefined) continue;
    if (!entity.visible) continue;
    if (entity.locked && options.includeLocked !== true) continue;
    if (options.exclude?.has(id) === true) continue;

    const kind = kindOf(entity.source);
    if (options.filter !== undefined && !options.filter(id, kind)) continue;

    const value = valueOf(scene, id);
    if (value === null) continue;

    const measured = distanceToValue(value, world);
    if (measured === null || measured.distance > tolerance) continue;

    hits.push({
      id,
      kind,
      distance: measured.distance,
      closest: measured.closest,
      order: ranks.get(id) ?? 0,
    });
  }

  hits.sort((a, b) => {
    const priority = KIND_PRIORITY[a.kind] - KIND_PRIORITY[b.kind];
    if (priority !== 0) return priority;
    if (Math.abs(a.distance - b.distance) > 1e-9) return a.distance - b.distance;
    return b.order - a.order;
  });

  return hits.map(({ id, kind, distance, closest }) => ({ id, kind, distance, closest }));
}

/** The single best hit, or `null`. */
export function hitTest(
  scene: Scene,
  world: Vec2,
  tolerance: number,
  options: HitTestOptions = {}
): Hit | null {
  return hitTestAll(scene, world, tolerance, options)[0] ?? null;
}

/** Every entity whose geometry falls entirely inside `region` — a marquee selection. */
export function entitiesWithin(
  scene: Scene,
  region: { minX: number; minY: number; maxX: number; maxY: number }
): readonly EntityId[] {
  const result: EntityId[] = [];

  /*
   * A marquee only ever captures what it covers, so the index narrows the field the same way
   * it does for picking. `inDrawOrder` still supplies the order — a marquee returns its
   * entities bottom-to-top, and callers rely on that for grouping.
   */
  const covered = queryIndexBounds(scene.index, region);

  for (const entity of inDrawOrder(scene.document)) {
    if (!covered.has(entity.id)) continue;
    if (!entity.visible || entity.locked) continue;
    const value = valueOf(scene, entity.id);
    if (value === null) continue;

    const points = representativePoints(value);
    // Unbounded geometry has no containable extent, so a marquee never captures it.
    if (points === null || points.length === 0) continue;

    const inside = points.every(
      (point) =>
        point.x >= region.minX &&
        point.x <= region.maxX &&
        point.y >= region.minY &&
        point.y <= region.maxY
    );
    if (inside) result.push(entity.id);
  }
  return result;
}

/** Points that must all be inside a marquee for the entity to count as enclosed. */
function representativePoints(value: Evaluated): readonly Vec2[] | null {
  switch (value.kind) {
    // A viewpoint: no geometry on the page at all.
    case 'space':
      return null;

    // Every sampled point, so a marquee captures a graph only when it encloses the whole of
    // what was drawn — the same rule every other bounded shape follows.
    case 'function-graph':
      return value.segments.flat();

    // The dots, not the guides: a guide reaches the axis, so requiring those inside would mean
    // a marquee had to contain the origin to select marks in a corner of the figure.
    case 'graph-marks':
      return value.points;

    case 'axes':
      return [value.xAxis.a, value.xAxis.b, value.yAxis.a, value.yAxis.b];

    // Its four corners: a marquee takes the table only when it encloses the whole block.
    case 'variation-table':
      return [
        value.corner,
        vec.vec2(value.corner.x + value.width, value.corner.y),
        vec.vec2(value.corner.x, value.corner.y - value.height),
        vec.vec2(value.corner.x + value.width, value.corner.y - value.height),
      ];

    case 'point':
      return [value.point];

    case 'angle-mark':
      return [
        value.vertex,
        angleMarkPickPoint(value),
        // A dragged number can sit outside everything else the mark occupies, and a marquee
        // that took the mark while leaving its number behind would be lying about what it caught.
        ...(value.showValue ? [angleMarkValuePosition(value) ?? value.vertex] : []),
      ];

    // Every drawn vertex must be inside the marquee, so a solid is only caught when it is
    // wholly enclosed — the same rule the other shapes follow.
    case 'solid':
      return [...value.visible, ...value.hidden].flatMap((edge) => [edge.a, edge.b]);
    case 'text':
    case 'measurement':
      return [value.position];

    // All four corners, so a picture is caught only when the marquee encloses the whole of it —
    // the same rule every other shape follows.
    case 'image':
      return [
        value.position,
        vec.vec2(value.position.x + value.width, value.position.y),
        vec.vec2(value.position.x, value.position.y + value.height),
        vec.vec2(value.position.x + value.width, value.position.y + value.height),
      ];
    case 'segment':
      return [value.segment.a, value.segment.b];
    case 'vector':
      return [value.from, value.to];
    case 'polygon':
      return value.vertices;
    case 'polyline':
      return value.points;
    case 'circle': {
      const { center, radius } = value.circle;
      return [
        { x: center.x - radius, y: center.y - radius },
        { x: center.x + radius, y: center.y + radius },
      ];
    }
    case 'arc':
      return [
        conics.arcStartPoint(value.arc),
        conics.arcEndPoint(value.arc),
        conics.arcMidpoint(value.arc),
      ];
    case 'ellipse': {
      const { center, rx, ry } = value.ellipse;
      const extent = Math.max(rx, ry);
      return [
        { x: center.x - extent, y: center.y - extent },
        { x: center.x + extent, y: center.y + extent },
      ];
    }
    case 'ray':
    case 'line':
      return null;
  }
}

/**
 * Where an angle mark is picked: out along its bisector, where the arc is drawn.
 *
 * Falls back to the vertex for a straight angle, whose two arms cancel and leave no
 * bisector — a mark on 180° is a degenerate case the user is unlikely to make, and a point
 * at the vertex is a better answer than a direction of zero length.
 */
function angleMarkPickPoint(value: Extract<Evaluated, { kind: 'angle-mark' }>): Vec2 {
  const bisector = vec.add(value.from, value.to);
  if (vec.isZero(bisector)) return value.vertex;

  /*
   * The radius the mark is *drawn* at, which is not `armLength / 3`.
   *
   * It used to be, and that made the mark unselectable on the very first figure a user meets.
   * The arc is clamped to a nominal size as well as to a fraction of the arm, so on the demo
   * document's right angle it is drawn 22 units from the vertex while this put the pick point at
   * 43 — a 21-unit gap against a pick radius of 8. Clicking the square found nothing, so its
   * panel never opened and neither its size, its ticks nor its value label could be changed.
   *
   * `size` was missing too, so a mark set to "rất to" was picked where an ordinary one would be.
   */
  const radius = Math.max(1, angleMarkRadius(value.armLength, value.size));
  return vec.add(value.vertex, vec.scale(vec.normalize(bisector), radius));
}
